import {
  BzAddress,
  BzDateFns,
  bzExpect,
  CompanyGuid,
  getEarliestAvailableDate,
  getNumUnusedMaintenancePlanVisits,
  HtmlString,
  LocalDateString,
  LocalTimeString,
  OnlineBookingServiceTypeDescriptions,
  OnlineBookingServiceTypeDisplayNames,
  OnlineBookingType,
  phoneUtils,
  stripHtml,
  ThisShouldNeverHappenError,
  UnauthedMaintenancePlanDetailsViewModel,
} from '@breezy/shared'

import { faScrewdriverWrench } from '@fortawesome/pro-regular-svg-icons'
import { faEnvelope, faLocationDot, faPhone, faUser } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Form, message, Popover, Typography } from 'antd'
import classNames from 'classnames'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { TextAreaField } from '../../elements/Forms/TextAreaField'
import { useReactHookFormSubmit } from '../../elements/Forms/useReactHookFormSubmit'
import { HtmlRenderer } from '../../elements/HtmlRenderer/HtmlRenderer'
import Switch from '../../elements/Switch/Switch'
import ThinDivider from '../../elements/ThinDivider'
import { trpc } from '../../hooks/trpc'
import { OnsiteConfirmModal } from '../../ui/OnsiteModal/OnsiteModal'
import { OnsitePageSection } from '../../ui/OnsitePage/OnsitePageSection'
import { SectionedContent } from '../../ui/SectionedCard/SectionedContent'
import { StepProgressIndicators } from '../../ui/StepProgressIndicators/StepProgressIndicators'
import { isVisitNoteInSummary, mergeSummaryWithVisitNote } from '../../utils/jobSummaryMerge'
import { useModalState, useStrictContext, useToggleState } from '../../utils/react-utils'
import { useMaintenancePlanPricing } from '../CreateOrEditMaintenancePlanPage/hooks/useMaintenancePlanPricing'
import { MaintenancePlanInfoSection } from '../CreateOrEditMaintenancePlanPage/MaintenancePlanTypeOptionCard'
import { BookMaintenanceRequestDetailsSubForm } from './BookMaintenanceRequestDetailsSubForm'
import { InstantBookingIcon } from './InstantBookingIcon'
import { OnlineBookingAvailabilitySubForm } from './OnlineBookingAvailabilitySubForm'
import { OnlineBookingInstantBookingSubForm } from './OnlineBookingInstantBookingSubForm'
import {
  AvailabilitySubFormData,
  AvailabilitySubFormSchema,
  BookMaintenanceJobSummarySubFormData,
  BookMaintenanceJobSummarySubFormSchema,
  BookMaintenanceRequestDetailsSubFormData,
  BookMaintenanceRequestDetailsSubFormSchema,
  InstantBookingSubFormData,
  InstantBookingSubFormSchema,
  OnlineBookingCompanyConfigContext,
  OnlineBookingServiceTypeConfigContext,
  ServiceTypeConfig,
  useBookableJobTypes,
} from './onlineBookingUtils'

type BookMaintenanceFormProps = {
  companyGuid: CompanyGuid
  onSubmit: (bookingType: OnlineBookingType) => void
  visit: UnauthedMaintenancePlanDetailsViewModel
}

const DEFAULT_FORM_STAGES = [{ name: 'Request Details' }, { name: 'Availability' }, { name: 'Review Request' }] as const

const DEFAULT_STEP = 0

export const BookMaintenanceForm = React.memo<BookMaintenanceFormProps>(
  ({ companyGuid, onSubmit: externalOnSubmit, visit }) => {
    // if we have a jobTypeGuid, we're on the booking step, otherwise the user has to specify their job type
    const FORM_STAGES = useMemo(() => (visit.jobTypeGuid ? DEFAULT_FORM_STAGES.slice(1) : DEFAULT_FORM_STAGES), [visit])

    const [currentStep, setCurrentStep] = useState<number>(DEFAULT_STEP)
    const { tzId } = useStrictContext(OnlineBookingCompanyConfigContext)
    const onlineBookingServiceTypeConfigs = useStrictContext(OnlineBookingServiceTypeConfigContext)
    const onlineBookingCompanyConfig = useStrictContext(OnlineBookingCompanyConfigContext)

    const companyAppointmentArrivalWindows = useMemo(
      () => onlineBookingCompanyConfig.companyAppointmentArrivalWindows,
      [onlineBookingCompanyConfig],
    )

    const [unavailableOpen, openUnavailableModal, closeUnavailableModal] = useModalState(false)
    const currentFormStageName = useMemo(() => FORM_STAGES[currentStep].name, [currentStep, FORM_STAGES])

    const requestDetailsSubForm = useForm<BookMaintenanceRequestDetailsSubFormData>({
      resolver: zodResolver(BookMaintenanceRequestDetailsSubFormSchema),
      mode: 'onChange',
      reValidateMode: 'onChange',
      defaultValues: {
        requestType: 'MAINTENANCE',
        jobTypeGuid: visit.jobTypeGuid,
      },
    })

    const jobTypeGuid = requestDetailsSubForm.watch('jobTypeGuid')
    const requestType = requestDetailsSubForm.watch('requestType')

    const availabilitySubForm = useForm<AvailabilitySubFormData>({
      resolver: zodResolver(AvailabilitySubFormSchema),
      mode: 'onChange',
      reValidateMode: 'onChange',
    })

    const serviceTypeConfig = useMemo<ServiceTypeConfig | undefined>(
      () => onlineBookingServiceTypeConfigs[requestType],
      [requestType, onlineBookingServiceTypeConfigs],
    )

    const instantBookingSubForm = useForm<InstantBookingSubFormData>({
      resolver: zodResolver(InstantBookingSubFormSchema),
      mode: 'onChange',
      reValidateMode: 'onChange',
      defaultValues: {
        date: BzDateFns.formatLocalDate(
          getEarliestAvailableDate(
            BzDateFns.getTodayLocalDate(tzId),
            serviceTypeConfig?.earliestAvailability ?? '12 hours',
            tzId,
          ),
        ),
      },
    })

    const jobSummarySubForm = useForm<BookMaintenanceJobSummarySubFormData>({
      resolver: zodResolver(BookMaintenanceJobSummarySubFormSchema),
      mode: 'onChange',
      reValidateMode: 'onChange',
      defaultValues: {
        jobSummary: '',
      },
    })

    const jobSummary = jobSummarySubForm.watch('jobSummary')

    const [dismissedVisitNoteGuid, setDismissedVisitNoteGuid] = useState<string | undefined>(undefined)

    const selectedVisitNoteHtml = useMemo(() => {
      const visits = visit.maintenancePlansQueryable.visits ?? []
      return visits.find(v => v.visitGuid === visit.maintenancePlanVisitGuid)?.visitNote
    }, [visit.maintenancePlanVisitGuid, visit.maintenancePlansQueryable.visits])

    const visitNoteText = useMemo(
      () => stripHtml((selectedVisitNoteHtml ?? '') as HtmlString).trim(),
      [selectedVisitNoteHtml],
    )

    const toPlainTextSummary = useCallback(
      (value?: string | HtmlString) => stripHtml((value ?? '') as HtmlString).trim(),
      [],
    )

    useEffect(() => {
      if (!visit.maintenancePlanVisitGuid && !visitNoteText) return
      setDismissedVisitNoteGuid(undefined)
    }, [visit.maintenancePlanVisitGuid, visitNoteText])

    useEffect(() => {
      if (!visitNoteText) return
      if (dismissedVisitNoteGuid === visit.maintenancePlanVisitGuid) return
      const currentSummary = jobSummarySubForm.getValues('jobSummary')
      if (!currentSummary || !currentSummary.trim()) {
        jobSummarySubForm.setValue('jobSummary', visitNoteText, { shouldDirty: true })
      }
    }, [dismissedVisitNoteGuid, jobSummarySubForm, visitNoteText, visit.maintenancePlanVisitGuid])

    const visitNotePreview = useMemo(() => {
      if (!visitNoteText) return ''
      return visitNoteText.length > 100 ? `${visitNoteText.slice(0, 100)}…` : visitNoteText
    }, [visitNoteText])

    const shouldShowVisitNoteMergePrompt = useMemo(() => {
      if (!visitNoteText) return false
      if (!jobSummary || !jobSummary.trim()) return false
      if (dismissedVisitNoteGuid === visit.maintenancePlanVisitGuid) return false
      return !isVisitNoteInSummary(jobSummary, visitNoteText)
    }, [dismissedVisitNoteGuid, jobSummary, visit.maintenancePlanVisitGuid, visitNoteText])

    const mergeJobSummaryWithVisitNote = useCallback(
      (summary: string) => {
        const merged = mergeSummaryWithVisitNote({
          existingSummaryHtml: summary,
          visitNoteHtml: visitNoteText,
          mode: 'set-if-empty',
        }) as string | undefined
        return merged ? toPlainTextSummary(merged) : summary
      },
      [toPlainTextSummary, visitNoteText],
    )

    const handleReplaceWithVisitNote = useCallback(() => {
      const mergedSummary = mergeSummaryWithVisitNote({
        existingSummaryHtml: jobSummarySubForm.getValues('jobSummary'),
        visitNoteHtml: visitNoteText,
        mode: 'replace',
      })
      jobSummarySubForm.setValue('jobSummary', toPlainTextSummary(mergedSummary ?? ''), { shouldDirty: true })
      setDismissedVisitNoteGuid(undefined)
    }, [jobSummarySubForm, toPlainTextSummary, visitNoteText])

    const handleAppendVisitNote = useCallback(() => {
      const mergedSummary = mergeSummaryWithVisitNote({
        existingSummaryHtml: jobSummarySubForm.getValues('jobSummary'),
        visitNoteHtml: visitNoteText,
        mode: 'append',
      })
      jobSummarySubForm.setValue('jobSummary', toPlainTextSummary(mergedSummary ?? ''), { shouldDirty: true })
      setDismissedVisitNoteGuid(undefined)
    }, [jobSummarySubForm, toPlainTextSummary, visitNoteText])

    const handleDismissVisitNote = useCallback(() => {
      setDismissedVisitNoteGuid(visit.maintenancePlanVisitGuid)
    }, [visit.maintenancePlanVisitGuid])

    const instantBookingDate = instantBookingSubForm.watch('date')
    const instantBookingArrivalWindowGuid = instantBookingSubForm.watch('companyAppointmentArrivalWindowGuid')

    const bookableJobTypes = useBookableJobTypes(requestType)
    const selectedJobType = useMemo(
      () => bookableJobTypes.find(j => j.jobTypeGuid === jobTypeGuid),
      [bookableJobTypes, jobTypeGuid],
    )

    const selectedArrivalWindow = useMemo(() => {
      return companyAppointmentArrivalWindows.find(
        w => w.companyAppointmentArrivalWindowGuid === instantBookingArrivalWindowGuid,
      )
    }, [companyAppointmentArrivalWindows, instantBookingArrivalWindowGuid])

    const [submitRequestDetailsElement, triggerRequestDetailsSubmit] = useReactHookFormSubmit()
    const [submitAvailabilityElement, triggerAvailabilitySubmit] = useReactHookFormSubmit()
    const [submitInstantBookingElement, triggerInstantBookingSubmit] = useReactHookFormSubmit()

    // biome-ignore lint/correctness/useExhaustiveDependencies: setCurrentStep is a state setter and is stable
    const moveToNextStep = useCallback(() => {
      setCurrentStep(currStep => currStep + 1)
    }, [setCurrentStep])

    // biome-ignore lint/correctness/useExhaustiveDependencies: setCurrentStep is a state setter and is stable
    const moveToPreviousStep = useCallback(() => {
      setCurrentStep(currStep => currStep - 1)
    }, [setCurrentStep])

    const onRequestDetailsSubmit = useCallback(
      (data: BookMaintenanceRequestDetailsSubFormData) => {
        moveToNextStep()
      },
      [moveToNextStep],
    )

    const onAvailabilitySubmit = useCallback(
      (data: AvailabilitySubFormData) => {
        moveToNextStep()
      },
      [moveToNextStep],
    )

    const onInstantBookingSubmit = useCallback(
      (data: InstantBookingSubFormData) => {
        moveToNextStep()
      },
      [moveToNextStep],
    )

    const jobLeadsCreateMutation = trpc.jobLeads['unauth:job-leads:create'].useMutation()

    const instantBookingScheduleJobMutation =
      trpc.onlineBooking['unauth:online-booking:instant-booking:schedule-job'].useMutation()

    const submitJobRequest = useCallback(async () => {
      const availabilityFormValues = availabilitySubForm.getValues()
      const jobSummaryFormValues = jobSummarySubForm.getValues()
      const mergedJobSummary = mergeJobSummaryWithVisitNote(jobSummaryFormValues.jobSummary)

      const availabilities: string[] = [
        `${BzDateFns.localDateToFriendlyDateString(
          availabilityFormValues.preferredAvailabilityDate,
        )} (${availabilityFormValues.preferredAvailabilityTimeWindows.join(', ')})`,
      ]

      if (availabilityFormValues.backupAvailabilityDate) {
        if (
          availabilityFormValues.backupAvailabilityTimeWindows &&
          availabilityFormValues.backupAvailabilityTimeWindows.length
        ) {
          availabilities.push(
            `${BzDateFns.localDateToFriendlyDateString(
              availabilityFormValues.backupAvailabilityDate,
            )} (${availabilityFormValues.backupAvailabilityTimeWindows.join(', ')})`,
          )
        } else {
          availabilities.push(BzDateFns.localDateToFriendlyDateString(availabilityFormValues.backupAvailabilityDate))
        }
      }

      try {
        await jobLeadsCreateMutation.mutateAsync({
          companyGuid,
          preExistingLocationGuid: visit.maintenancePlansQueryable.locationGuid,
          requestType,
          submittedJobTypeGuid: jobTypeGuid,
          appointmentAvailabilities: availabilities,
          jobSummary: mergedJobSummary,
          maintenancePlanVisitGuid: visit.maintenancePlanVisitGuid,
          leadSource: 'Booking Website',
        })

        externalOnSubmit('REQUEST')
      } catch (e) {
        console.error(`There was an error creating a job lead`, e)
        message.error(`There was an error requesting the job. Please try again in a few minutes.`)
      }
    }, [
      availabilitySubForm,
      jobSummarySubForm,
      companyGuid,
      jobLeadsCreateMutation,
      externalOnSubmit,
      visit,
      requestType,
      jobTypeGuid,
      mergeJobSummaryWithVisitNote,
    ])

    const submitInstantBooking = useCallback(async () => {
      const instantBookingFormValues = instantBookingSubForm.getValues()
      const jobSummaryFormValues = jobSummarySubForm.getValues()
      const requestFormValues = requestDetailsSubForm.getValues()
      const mergedJobSummary = mergeJobSummaryWithVisitNote(jobSummaryFormValues.jobSummary)

      try {
        const result = await instantBookingScheduleJobMutation.mutateAsync({
          companyGuid,

          // Instant Booking Information
          date: instantBookingFormValues.date,
          companyAppointmentArrivalWindowGuid: instantBookingFormValues.companyAppointmentArrivalWindowGuid,
          preExistingLocationGuid: visit.maintenancePlansQueryable.locationGuid,
          serviceType: requestFormValues.requestType,
          jobTypeGuid: bzExpect(requestFormValues.jobTypeGuid, 'Missing job type guid'),
          jobSummary: mergedJobSummary,
          useMaintenancePlanCredit: true,
          maintenancePlanVisitGuid: visit.maintenancePlanVisitGuid,
          maintenancePlanGuid: visit.maintenancePlanGuid,
          leadSource: 'Booking Website',
        })

        if (result.type === 'ERROR') {
          console.error('Arrival window is no longer available', result.error)
          openUnavailableModal()
          return
        }

        externalOnSubmit('INSTANT')
      } catch (e) {
        console.error(`There was an error creating instant booking`, e)
        message.error(`There was an error creating instant booking. Please try again in a few minutes.`)
      }
    }, [
      instantBookingSubForm,
      jobSummarySubForm,
      requestDetailsSubForm,
      externalOnSubmit,
      instantBookingScheduleJobMutation,
      companyGuid,
      openUnavailableModal,
      visit,
      mergeJobSummaryWithVisitNote,
    ])

    const onSubmit = useCallback(() => {
      if (serviceTypeConfig?.bookingType === 'INSTANT') {
        submitInstantBooking()
      } else {
        submitJobRequest()
      }
    }, [serviceTypeConfig, submitInstantBooking, submitJobRequest])

    // biome-ignore lint/correctness/useExhaustiveDependencies: setCurrentStep is a state setter and is stable
    const onTimeWindowUnavailable = useCallback(() => {
      setCurrentStep(1)
      instantBookingSubForm.resetField('companyAppointmentArrivalWindowGuid')
      closeUnavailableModal()
    }, [setCurrentStep, instantBookingSubForm, closeUnavailableModal])

    const isBackButtonEnabled = useMemo(() => {
      return currentStep !== 0 && !jobLeadsCreateMutation.isLoading
    }, [currentStep, jobLeadsCreateMutation.isLoading])

    const maintenancePlanDefinition = useMemo(() => {
      return bzExpect(visit.maintenancePlansQueryable.planDefinition, 'Missing plan definition')
    }, [visit.maintenancePlansQueryable.planDefinition])

    const flareColor = useMemo(() => {
      return maintenancePlanDefinition.flare?.primaryColorHex ?? '#13C2C2'
    }, [maintenancePlanDefinition])

    const expiresAt = useMemo(() => {
      if (!visit.maintenancePlansQueryable.terminatesAt) {
        return undefined
      }

      return BzDateFns.localDateToFriendlyDateString(
        BzDateFns.convertToLocalDateString(visit.maintenancePlansQueryable.terminatesAt, tzId),
      )
    }, [visit.maintenancePlansQueryable.terminatesAt, tzId])

    const availableVisits = useMemo(() => {
      return getNumUnusedMaintenancePlanVisits(visit.maintenancePlansQueryable.visits ?? [])
    }, [visit.maintenancePlansQueryable.visits])

    const totalVisits = useMemo(() => {
      return visit.maintenancePlansQueryable.visits?.length ?? 0
    }, [visit.maintenancePlansQueryable.visits])

    const { pricingInformationPrimary, pricingInformationSecondary } = useMaintenancePlanPricing({
      maintenancePlanDefinition,
    })

    const onPrimaryCTAButtonClicked = useCallback(() => {
      switch (currentFormStageName) {
        case 'Request Details':
          triggerRequestDetailsSubmit()
          break
        case 'Availability':
          if (bzExpect(serviceTypeConfig, 'Missing service type config').bookingType === 'REQUEST') {
            triggerAvailabilitySubmit()
          } else {
            triggerInstantBookingSubmit()
          }

          break
        case 'Review Request':
          onSubmit()
          break
        default:
          throw new ThisShouldNeverHappenError('Impossible stage')
      }
    }, [
      currentFormStageName,
      onSubmit,
      serviceTypeConfig,
      triggerRequestDetailsSubmit,
      triggerAvailabilitySubmit,
      triggerInstantBookingSubmit,
    ])

    const reviewRequestData = useMemo(() => {
      if (currentFormStageName !== 'Review Request') {
        return undefined
      }

      return {
        name: visit.maintenancePlansQueryable.primaryContactDisplayName,
        address: BzAddress.create(visit.maintenancePlansQueryable.locationAddress).formatSingleLine(),
        phoneNumber: visit.maintenancePlansQueryable.primaryContactPhoneNumber,
        emailAddress: visit.maintenancePlansQueryable.primaryContactEmail,
      }
    }, [currentFormStageName, visit])

    const goBack = useCallback(() => {
      moveToPreviousStep()
    }, [moveToPreviousStep])

    return (
      <>
        <OnsitePageSection padding="large-mobile">
          <div className="flex flex-col">
            <Switch value={currentFormStageName}>
              {{
                'Request Details': () => <FormStepTitle>Request Details</FormStepTitle>,
                Availability: () => (
                  <>
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center gap-3">
                        <FormStepTitle>Availability</FormStepTitle>
                        {serviceTypeConfig?.bookingType === 'INSTANT' && <InstantBookingIcon />}
                      </div>
                      <div>
                        {`Select which date and ${
                          serviceTypeConfig?.bookingType === 'INSTANT' ? 'time' : 'times'
                        } would be best for a technician to come to your home.`}
                      </div>
                    </div>
                    <ThinDivider
                      widthPx={1}
                      styleOverrides={{
                        marginTop: '24px',
                        marginBottom: '24px',
                      }}
                      dividerStyle="dashed"
                    />
                  </>
                ),
                'Review Request': () => <FormStepTitle>Review Request</FormStepTitle>,
                default: () => null,
              }}
            </Switch>
            {currentFormStageName === 'Request Details' && (
              <FormProvider {...requestDetailsSubForm}>
                <Form
                  requiredMark="optional"
                  className="mt-[12px] flex min-h-0 flex-1 flex-col"
                  layout="vertical"
                  onSubmitCapture={requestDetailsSubForm.handleSubmit(onRequestDetailsSubmit)}
                >
                  <BookMaintenanceRequestDetailsSubForm />
                  {submitRequestDetailsElement}
                </Form>
              </FormProvider>
            )}
            {currentFormStageName === 'Availability' && (
              <Switch value={serviceTypeConfig?.bookingType}>
                {{
                  REQUEST: () => (
                    <FormProvider {...availabilitySubForm}>
                      <Form
                        className="mt-[12px] flex min-h-0 flex-1 flex-col"
                        layout="vertical"
                        onSubmitCapture={availabilitySubForm.handleSubmit(onAvailabilitySubmit)}
                        requiredMark="optional"
                      >
                        <OnlineBookingAvailabilitySubForm />
                        {submitAvailabilityElement}
                      </Form>
                    </FormProvider>
                  ),
                  INSTANT: () =>
                    serviceTypeConfig ? (
                      <FormProvider {...instantBookingSubForm}>
                        <Form
                          className="mt-[12px] flex min-h-0 flex-1 flex-col"
                          layout="vertical"
                          onSubmitCapture={instantBookingSubForm.handleSubmit(onInstantBookingSubmit)}
                          requiredMark="optional"
                        >
                          <OnlineBookingInstantBookingSubForm
                            companyGuid={companyGuid}
                            serviceType={requestType}
                            serviceTypeConfig={serviceTypeConfig}
                            jobTypeGuid={jobTypeGuid}
                          />
                          {submitInstantBookingElement}
                        </Form>
                      </FormProvider>
                    ) : null,
                  default: () => null,
                }}
              </Switch>
            )}

            {currentFormStageName === 'Review Request' && reviewRequestData && (
              <SectionedContent
                dashed
                sections={[
                  {
                    verticalPaddingClassName: 'pt-3 pb-6',
                    content: (
                      <div>
                        <div className="flex flex-col gap-1">
                          <div>Please review your contact information below:</div>
                          <div className="flex gap-3">
                            <div className="w-4">
                              <FontAwesomeIcon size="sm" icon={faUser} className="text-bz-gray-700" />
                            </div>
                            <div className="font-semibold text-[#000000E0]">{reviewRequestData.name}</div>
                          </div>
                          {reviewRequestData.phoneNumber && (
                            <div className="flex gap-3">
                              <div className="w-4">
                                <FontAwesomeIcon size="sm" icon={faPhone} className="text-bz-gray-700" />
                              </div>
                              <div className="font-semibold text-[#000000E0]">
                                {phoneUtils.tryFormatWithFallback(
                                  reviewRequestData.phoneNumber,
                                  reviewRequestData.phoneNumber,
                                )}
                              </div>
                            </div>
                          )}

                          {reviewRequestData.emailAddress && (
                            <div className="flex gap-3">
                              <div className="w-4">
                                <FontAwesomeIcon size="sm" icon={faEnvelope} className="text-bz-gray-700" />
                              </div>
                              <div className="font-semibold text-[#000000E0]">{reviewRequestData.emailAddress}</div>
                            </div>
                          )}

                          <div className="mb-4 flex gap-3">
                            <div className="w-4">
                              <FontAwesomeIcon size="sm" icon={faLocationDot} className="text-bz-gray-700" />
                            </div>
                            <div className="font-semibold text-[#000000E0]">{reviewRequestData.address}</div>
                          </div>
                          <div
                            className={`flex items-start rounded-xl border-b border-l border-r border-solid border-bz-border bg-white p-4 shadow-bz-table`}
                            style={{ borderTop: `8px solid ${flareColor}` }}
                          >
                            <div
                              className="mr-4 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-bz-fill-tertiary"
                              style={{ color: flareColor }}
                            >
                              <FontAwesomeIcon icon={faScrewdriverWrench} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="semibold_16_24 text-bz-text">You are a Gold Plan Member</div>
                              <div className="regular_16_24 mt-1 text-bz-text">
                                {expiresAt ? (
                                  <>
                                    This plan expires on <span className="font-medium">{expiresAt}</span> and has{' '}
                                  </>
                                ) : (
                                  <>This plan has </>
                                )}
                                <span className="font-medium">
                                  {availableVisits} of {totalVisits}
                                </span>{' '}
                                visits remaining.
                              </div>
                              <div>
                                <Popover
                                  placement="top"
                                  content={
                                    <div className="flex flex-col p-1">
                                      <div className="mb-3 text-[16px] font-semibold">Plan Details</div>
                                      <MaintenancePlanInfoSection
                                        maintenancePlanDefinition={maintenancePlanDefinition}
                                      />
                                      <ThinDivider />
                                      <div className="flex flex-row">
                                        <Typography.Text className="mr-2 text-base font-semibold text-[#000]">
                                          {pricingInformationPrimary}
                                        </Typography.Text>
                                        <Typography.Text className="text-base font-normal text-[#8c8c8c]">
                                          {pricingInformationSecondary}
                                        </Typography.Text>
                                      </div>
                                    </div>
                                  }
                                >
                                  <div className="mt-2 w-fit min-w-[240px] cursor-pointer text-[14px] leading-[22px] text-[#1677fe]">
                                    View Plan Benefits
                                  </div>
                                </Popover>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ),
                  },
                  {
                    verticalPaddingClassName: 'pt-6 pb-0',
                    content: (
                      <div className="flex flex-col gap-4">
                        <div className="flex flex-col gap-1">
                          <div className="font-semibold text-[#1f1f1f] ">Request Type</div>
                          <div className="text-[#1f1f1f] ">{OnlineBookingServiceTypeDescriptions[requestType]}</div>
                        </div>
                        {selectedJobType && (
                          <div className="flex flex-col gap-1">
                            <div className="font-semibold text-[#1f1f1f] ">
                              {`${OnlineBookingServiceTypeDisplayNames[requestType]} Type`}
                            </div>
                            <div className="text-[#1f1f1f] ">{selectedJobType.name}</div>
                          </div>
                        )}
                        <Switch value={serviceTypeConfig?.bookingType}>
                          {{
                            INSTANT: () =>
                              selectedArrivalWindow &&
                              instantBookingDate && (
                                <div className="flex flex-col gap-1">
                                  <div className="font-semibold text-[#1f1f1f]">Date & Arrival Window</div>
                                  <div className="text-[#1f1f1f]">
                                    {`${BzDateFns.format(
                                      BzDateFns.parseLocalDate(instantBookingDate),
                                      'EEEE, MMM d,',
                                    )} ${BzDateFns.localTimeStringToFormattedTimeString(
                                      selectedArrivalWindow.arrivalWindowStartTime,
                                      false,
                                    )} - ${BzDateFns.localTimeStringToFormattedTimeString(
                                      selectedArrivalWindow.arrivalWindowEndTime,
                                      false,
                                    )}`}
                                  </div>
                                </div>
                              ),
                            REQUEST: () => (
                              <>
                                {' '}
                                <div className="flex flex-col gap-1">
                                  <div className="font-semibold text-[#1f1f1f] ">Preferred Date #1</div>
                                  <div className="text-[#1f1f1f] ">
                                    {BzDateFns.localDateToFriendlyDateString(
                                      availabilitySubForm.getValues().preferredAvailabilityDate,
                                    )}{' '}
                                    {'('}
                                    {availabilitySubForm.getValues().preferredAvailabilityTimeWindows.join(', ')}
                                    {')'}
                                  </div>
                                </div>
                                {availabilitySubForm.getValues().backupAvailabilityDate && (
                                  <div className="flex flex-col gap-1">
                                    <div className="font-semibold text-[#1f1f1f] ">Preferred Date #2</div>
                                    <div className="text-[#1f1f1f] ">
                                      {BzDateFns.localDateToFriendlyDateString(
                                        availabilitySubForm.getValues().backupAvailabilityDate!,
                                      )}
                                      {availabilitySubForm.getValues().backupAvailabilityTimeWindows && (
                                        <>
                                          {' '}
                                          {'('}
                                          {availabilitySubForm.getValues().backupAvailabilityTimeWindows!.join(', ')}
                                          {')'}
                                        </>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </>
                            ),
                            default: () => null,
                          }}
                        </Switch>
                        <Form layout="vertical" requiredMark="optional">
                          <Form.Item name="jobSummary" label="Additional Details">
                            <TextAreaField
                              name="jobSummary"
                              rows={4}
                              value={jobSummary}
                              onChange={e =>
                                jobSummarySubForm.setValue('jobSummary', e.target.value, { shouldDirty: true })
                              }
                            />
                          </Form.Item>
                          {shouldShowVisitNoteMergePrompt ? (
                            <div className="mt-2 rounded-md border border-bz-border bg-bz-fill p-3">
                              <div className="text-sm font-semibold text-bz-text">Visit note available</div>
                              <div className="text-sm text-bz-text-secondary">
                                {visitNotePreview || 'This visit includes additional instructions.'}
                              </div>
                              <div className="mt-2 flex flex-wrap gap-2">
                                <Button size="small" onClick={handleReplaceWithVisitNote}>
                                  Replace summary
                                </Button>
                                <Button size="small" type="primary" onClick={handleAppendVisitNote}>
                                  Append note
                                </Button>
                                <Button size="small" type="text" onClick={handleDismissVisitNote}>
                                  Dismiss
                                </Button>
                              </div>
                            </div>
                          ) : null}
                        </Form>
                      </div>
                    ),
                  },
                ]}
              />
            )}
          </div>
        </OnsitePageSection>

        <OnsitePageSection padding="large-mobile">
          <div className="flex items-center justify-between">
            <StepProgressIndicators
              currentStep={currentStep}
              totalSteps={FORM_STAGES.length}
              onStepIndicatorClicked={(step: number) => setCurrentStep(step)}
              colorHex={onlineBookingCompanyConfig?.brandingColorHex}
            />
            <div className="flex flex-row gap-[12px]">
              <Button disabled={!isBackButtonEnabled} onClick={goBack} size="large">
                Back
              </Button>
              <Button
                className="min-w-[140px]"
                block
                size="large"
                type="primary"
                style={{
                  backgroundColor: onlineBookingCompanyConfig?.brandingColorHex,
                }}
                onClick={onPrimaryCTAButtonClicked}
                loading={jobLeadsCreateMutation.isLoading || instantBookingScheduleJobMutation.isLoading}
              >
                {currentFormStageName === 'Review Request' ? 'Submit' : 'Next'}
              </Button>
            </div>
          </div>
        </OnsitePageSection>
        {currentFormStageName === 'Review Request' && serviceTypeConfig?.legalBlurbHtml && (
          <OnsitePageSection padding="large-mobile">
            <FormStepTitle>Terms & Conditions</FormStepTitle>
            <TermsAndConditions terms={serviceTypeConfig.legalBlurbHtml} />
          </OnsitePageSection>
        )}
        {currentFormStageName === 'Review Request' && unavailableOpen && selectedArrivalWindow && (
          <TimeWindowUnavailableWindow
            date={instantBookingDate}
            arrivalWindowStart={selectedArrivalWindow.arrivalWindowStartTime}
            arrivalWindowEnd={selectedArrivalWindow.arrivalWindowEndTime}
            onCancel={closeUnavailableModal}
            onConfirm={onTimeWindowUnavailable}
          />
        )}
      </>
    )
  },
)

BookMaintenanceForm.displayName = 'BookMaintenanceForm'

const FormStepTitle = React.memo<React.PropsWithChildren<{ className?: string }>>(({ children, className }) => {
  return (
    <div
      className={classNames(
        'break-words text-[20px] font-[600] leading-[28px] text-[rgba(0,0,0,0.88)] text-bz-text',
        className,
      )}
    >
      {children}
    </div>
  )
})

type TermsAndConditionsProps = {
  terms: HtmlString
}

export const TermsAndConditions = React.memo<TermsAndConditionsProps>(({ terms }) => {
  const [blurbExpanded, toggle] = useToggleState(false)

  return (
    <>
      <div
        className={classNames('mt-4', {
          'line-clamp-5': !blurbExpanded,
        })}
      >
        <HtmlRenderer htmlContent={terms} />
      </div>
      <div onClick={toggle} className={classNames('cursor-pointer underline transition-colors hover:text-bz-gray-700')}>
        {blurbExpanded ? 'See less' : 'Read more...'}
      </div>
    </>
  )
})

TermsAndConditions.displayName = 'TermsAndConditions'

type TimeWindowUnavailableWindowProps = {
  date: LocalDateString
  arrivalWindowStart: LocalTimeString
  arrivalWindowEnd: LocalTimeString
  onCancel: () => void
  onConfirm: () => void
}

const TimeWindowUnavailableWindow = React.memo<TimeWindowUnavailableWindowProps>(
  ({ date, arrivalWindowStart, arrivalWindowEnd, onCancel, onConfirm }) => {
    return (
      <OnsiteConfirmModal
        open
        header="Time unavailable"
        confirmText="Select New Time"
        onCancel={onCancel}
        onConfirm={onConfirm}
      >
        <span>Unfortunately, the</span>
        <span className="font-semibold">
          {` ${BzDateFns.localTimeStringToFormattedTimeString(
            arrivalWindowStart,
            false,
          )} - ${BzDateFns.localTimeStringToFormattedTimeString(arrivalWindowEnd, false)} `}
        </span>
        <span>arrival window you selected for</span>
        <span className="font-semibold">{` ${BzDateFns.format(BzDateFns.parseLocalDate(date), 'MMMM d, yyyy')} `}</span>
        <span>
          is no longer available. Please choose a different date or time. We apologize for any inconvenience and
          appreciate your understanding.
        </span>
      </OnsiteConfirmModal>
    )
  },
)
